import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";
import toast from "react-hot-toast";

import SignaturePad from "../../../Component/shared/signaturePad";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";

import "./AcupunctureConsent.css";
import { GetCaseFormListData, SaveCaseFormData } from "../Service";
import {
  AcupunctureConsentConstant,
  IntakeSheetSession,
  RoleType,
} from "../../../helpers/constant";
import { getSessionStorage } from "../../../helpers/storage";
import { CurrentProviderData } from "../../../Constant";
import { GragIntakeSheetData } from "../IntakeSheetUtils";
import {
  CallPrintApiForIntakeSheet,
  getFile,
  GetTokenInfo,
  removeIntakeSheets,
} from "../assignmentOfBenefit/Service";
import { usePatientData } from "../../../commonFunction/storage/Patientdatacontext";

const SHARED_SIG_KEY = "sharedPatientSignature";

function setInCollection(collection, name, value) {
  const item = collection.find((x) => x.Name === name);
  if (item) item.Value = value;
}

const AcupunctureConsent = forwardRef(function AcupunctureConsent(props, ref) {
  const {
    providerOptions = [],
    sessionData = {},
    signatureMiss,
    copySignature,
    selectedRow = {},
    intakeSheet = {},
    BaseUrl = "",
  } = props;

  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  const providerData = getSessionStorage(CurrentProviderData) || [];
  const roleName = sessionData?.Role?.Name?.trim().toLowerCase() ?? "";
  const isFrontDesk =
    roleName === "frontdesk" ||
    roleName === "front desk" ||
    roleName === "admin";

  /* ── state ── */
  const [loading, setLoading] = useState(false);
  const [CaseFormDataId, setCaseFormDataId] = useState("");
  const [collectionOfEPAT, setCollectionOfEPAT] = useState([]);
  const [BeforeParsingEPATData, setBeforeParsingEPATData] = useState({});

  const [patientName, setPatientName] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [relationToPatient, setRelationToPatient] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [patientSignImg, setPatientSignImg] = useState("");
  const [providerSignImg, setProviderSignImg] = useState("");
  const [patientSignDate, setPatientSignDate] = useState(new Date());
  const [providerSignDate, setProviderSignDate] = useState(new Date());
  const [selectedProvider, setSelectedProvider] = useState( providerOptions[0] || null );

  const [sigPadOpen, setSigPadOpen] = useState(false);
  const [signRole, setSignRole] = useState("");

  const { patientData, fetchPatientData } = usePatientData();

  const stateRef = useRef({});
  useEffect(() => {
    stateRef.current = {
      collectionOfEPAT,
      BeforeParsingEPATData,
      CaseFormDataId,
      patientName,
      guardianName,
      relationToPatient,
      assignTo,
      patientSignImg,
      providerSignImg,
      patientSignDate,
      providerSignDate,
      selectedProvider,
      patientData,
    };
  });

  useImperativeHandle(
    ref,
    () => ({
      save: () => saveFunction(),
      isPending: () => !stateRef.current.patientSignImg,
      getSignature: () => stateRef.current.patientSignImg || "",
      applySignature: (sig) => {
        if (!sig) return;
        setPatientSignImg(sig);
        setPatientSignDate(new Date());
      },
    }),
  );

  useEffect(() => {
    props?.onReady?.("acupuncture_consent");
  }, [patientSignImg]);

  /* ── helpers ── */
  const filterData = (key, col = collectionOfEPAT) => {
    const item = col.find((x) => x.Name === key);
    const val = item ? item.Value : "";
    return val === "01/01/1900" ? "" : val;
  };

  const fmt = (d) =>
    !d ? "" : moment(d).format("MM-DD-YYYY").split("-").join("/");

  const parseDateSafe = (raw) =>
    isNaN(new Date(raw).getTime()) ? "" : new Date(raw);

  const normalise = (raw) =>
    !raw
      ? ""
      : raw.includes(IntakeSheetSession.dataBlock)
        ? raw
        : IntakeSheetSession.dataBlock + raw;

  const viewSign = async (result) => {
    try {
      const requestBody = `?${result}`;
      const res = await getFile(requestBody);
      if (!res.ok) {
        toast.error("Something went wrong");
        return "";
      }
      const blob = await res.blob();
      if (blob.size === 0) {
        toast.error("No Files Found On Server");
        return "";
      }
      const fileURL = URL.createObjectURL(blob);
      const respBlob = await fetch(fileURL).then((r) => r.blob());
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () =>
          resolve(`data:image/png;base64,${reader.result.split(",")[1]}`);
        reader.readAsDataURL(respBlob);
      });
    } catch (err) {
      console.error("viewSign error:", err);
      return "";
    }
  };

  const getProviderSign = async (providerId) => {
    setLoading(true);
    try {
      const TokenInfo = GetTokenInfo();
      const response = await fetch(
        `${BaseUrl}ProviderMaster/GetSignPath?id=${providerId}&CompanyId=${tokenData.companyId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + TokenInfo.AccessToken,
          },
        },
      );
      if (response.status === 401) {
        setLoading(false);
        return null;
      }
      const result = await response.json();
      if (result.HasException) {
        setLoading(false);
        return "";
      }
      if (!result) {
        setLoading(false);
        return "";
      }
      const sign = await viewSign(result);
      setLoading(false);
      return sign;
    } catch (err) {
      console.error("getProviderSign error:", err);
      setLoading(false);
      return "";
    }
  };

  const handleGetCaseFormData = async () => {
    try {
      const requestBody = {
        CaseFormDataId: null,
        CaseId: tokenData.caseId,
        CompanyId: tokenData.companyId,
        DoctorId: "ALL",
        EntityLevel: 0,
        FormId: 25,
        FormName: "Acupuncture Consent",
        FormType: null,
        IsFormCompleted: false,
        JsonData: null,
        TemplateName: null,
        UserId: tokenData.userId,
      };
      const response = await GetCaseFormListData(requestBody);
      if (response?.HasException) {
        toast.error(
          `Something went wrong. ${response?.MessageText}. ${response?.Exception}`,
        );
        return {};
      }
      return response?.Data?.[0] || {};
    } catch (error) {
      toast.error(error.message);
      return {};
    }
  };

  /* ── save API — no internal setLoading, saveFunction owns it ── */
  const CallSaveApiForIntakeSheet = async (jsonData) => {
    try {
      const s = stateRef.current;
      const payload = {
        CaseFormDataId: s.CaseFormDataId || null,
        CaseId: tokenData.caseId,
        CompanyId: tokenData.companyId,
        DoctorId: tokenData.doctorId || tokenData.DoctorId || "",
        EntityLevel: 0,
        FormId: 25,
        FormName: "Acupuncture Consent",
        FormType: null,
        IsChanged: false,
        IsCopySign: false,
        IsFormCompleted: false,
        TemplateName: null,
        UserId: tokenData.userId,
        officeId: tokenData.providerId,
        JsonData: jsonData,
      };
      const response = await SaveCaseFormData(payload);
      if (response?.HasException) {
        toast.error(
          `Error while saving Acupuncture Consent. ${response?.MessageText || ""}`,
        );
        return false;
      }
      if (response?.Data?.[0]?.CaseFormDataId) {
        setCaseFormDataId(response.Data[0].CaseFormDataId);
      }
      toast.success("Acupuncture Consent saved successfully.");
      return true;
    } catch (err) {
      toast.error("Error while saving Acupuncture Consent data.");
      return false;
    }
  };

  /* ── mount ── */
  useEffect(() => {
    callApi();
  }, [patientData]);

  const callApi = async () => {
    setLoading(true);
    const formData = await handleGetCaseFormData();
    const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);

    if (!formData?.JsonData) {
      setLoading(false);
      return;
    }

    const acuData = JSON.parse(formData.JsonData);
    const collection = GragIntakeSheetData(acuData);

    setCollectionOfEPAT(collection);
    setBeforeParsingEPATData(acuData);
    setCaseFormDataId(formData.CaseFormDataId);

    await bindValueToControl(collection, acuData, patientInfo);
    setLoading(false);
  };

  const bindValueToControl = async (col, _data, patientInfo) => {
    const savedPatientName =
      filterData(AcupunctureConsentConstant.PatientName, col) ||
      patientInfo?.Name ||
      (selectedRow?.searchResult?.Patient?.Name ??
        selectedRow?.data?.Name ??
        "");

    setPatientName(patientData?.patientFirstName + ' ' + patientData?.patientLastName || '');
    setGuardianName(filterData(AcupunctureConsentConstant.GuardianName, col));
    setRelationToPatient(
      filterData(AcupunctureConsentConstant.PatientRelation, col),
    );

    /* provider resolution */
    let selProvider = providerOptions[0] || null;
    if (isFrontDesk) {
      const proName =
        filterData(AcupunctureConsentConstant.ProviderName, col) ||
        providerOptions[0]?.officeName ||
        providerOptions[0]?.label ||
        "";
      const found = providerOptions.find(
        (x) => x?.label === proName || x?.officeName === proName,
      );
      selProvider = found || providerOptions[0] || null;
      setSelectedProvider(selProvider);
      setAssignTo(selProvider?.officeName || selProvider?.label || "");
    } else {
      const savedProvName =
        filterData(AcupunctureConsentConstant.ProviderName, col) ||
        providerData[0]?.ProviderName ||
        "";
      setAssignTo(savedProvName);
      setSelectedProvider(selProvider);
    }
    const savedProviderDate = parseDateSafe(
      filterData(AcupunctureConsentConstant.ProviderSignDate, col),
    );

    setProviderSignDate(savedProviderDate || null);

    /* signs */
    const patSignRaw = filterData(AcupunctureConsentConstant.PatientSign, col);
    const savedSig = copySignature
      ? sessionStorage.getItem(SHARED_SIG_KEY)
      : null;
    const patSign = savedSig || normalise(patSignRaw);

    let provSign = "";
    let proSignDate = "";
    const provSignInCollection = filterData(
      AcupunctureConsentConstant.ProviderSign,
      col,
    );

    if (provSignInCollection) {
      provSign = normalise(provSignInCollection);
    } else {
      if (isFrontDesk && selProvider) {
        provSign = await getProviderSign(selProvider.value);
      } else if (!isFrontDesk && providerData[0]) {
        provSign = await getProviderSign(providerData[0].ProviderId);
      }
    }

    setPatientSignImg(patSign);
    setProviderSignImg(provSign);
    setPatientSignDate(
      parseDateSafe(
        filterData(AcupunctureConsentConstant.PatientSignDate, col),
      ),
    );
    setProviderSignDate(
      parseDateSafe(
        filterData(AcupunctureConsentConstant.ProviderSignDate, col),
      ) || null,
    );
    if (!patSign) signatureMiss?.(true);
  };

  /* ── provider dropdown ── */
  const onProviderChange = async (option) => {
    if (!option) return;
    setSelectedProvider(option);
    setAssignTo(option.officeName || option.label || "");
    const sign = await getProviderSign(option.value);
    setProviderSignImg(sign);
    setProviderSignDate(sign ? new Date() : null);
  };

  /* ── signature pad handlers ── */
  const clickPatientSign = () => {
    setSignRole(RoleType.Patient);
    setSigPadOpen(true);
  };

  const currentSig =
    signRole === RoleType.provider ? providerSignImg : patientSignImg;

  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(
          IntakeSheetSession.dataBlock1,
          IntakeSheetSession.dataBlock,
        )
      : base64Img;
    if (signRole === RoleType.provider) {
      setProviderSignImg(newBase);
      setProviderSignDate(new Date());
    } else {
      setPatientSignImg(newBase);
      setPatientSignDate(new Date());
      sessionStorage.setItem(SHARED_SIG_KEY, newBase);
    }
  };

  const clearSignCall = () => {
    if (signRole === RoleType.provider) {
      setProviderSignImg("");
      setProviderSignDate("");
    } else {
      setPatientSignImg("");
      setPatientSignDate("");
      signatureMiss?.(true);
    }
  };

  /* ── update collection ── */
  const updateCollection = (collection, vals) => {
    const currentVals = vals || stateRef.current;
    const updated = [...(collection || [])];
    const set = (key, value) => setInCollection(updated, key, value);
    const provVal =
      currentVals.assignTo ||
      currentVals.selectedProvider?.officeName ||
      currentVals.selectedProvider?.label ||
      tokenData?.providerName ||
      "";

    set(AcupunctureConsentConstant.PatientName, currentVals.patientName);
    set(AcupunctureConsentConstant.PatientRelation, currentVals.relationToPatient);
    set(AcupunctureConsentConstant.GuardianName, currentVals.guardianName);
    set(AcupunctureConsentConstant.PatientSign, currentVals.patientSignImg);
    set(AcupunctureConsentConstant.ProviderSign, currentVals.providerSignImg);
    set(AcupunctureConsentConstant.PatientSignDate, fmt(currentVals.patientSignDate));
    set(AcupunctureConsentConstant.ProviderSignDate, fmt(currentVals.providerSignDate));
    set(AcupunctureConsentConstant.ProviderName, provVal);
    return updated;
  };

  const formJsonRequest = async (updatedCollection, beforeData) => {
    try {
      if (!beforeData || !beforeData.notesdata) {
        console.error("No notesdata found in beforeData");
        return false;
      }
      // Clone first to avoid mutating state
      const bdClone = JSON.parse(JSON.stringify(beforeData));

      for (let i = 0; i < bdClone.notesdata.length; i++) {
        if (!bdClone.notesdata[i].ApplicationValue) continue;

        for (let j = 0; j < bdClone.notesdata[i].ApplicationValue.length; j++) {
          const appVal = bdClone.notesdata[i].ApplicationValue[j];
          const matchItem = updatedCollection.find(
            (x) => x.Name === appVal.Name,
          );

          if (!matchItem) continue;

          appVal.Type = matchItem.Type;
          appVal.isRender = matchItem.isRender;

          const isSign =
            appVal.Name === AcupunctureConsentConstant.PatientSign ||
            appVal.Name === AcupunctureConsentConstant.ProviderSign;

          if (isSign) {
            const val = matchItem.Value;
            appVal.Value = val
              ? val.includes(";")
                ? val.split(";")[1].split(",")[1]
                : val
              : "";
          } else {
            appVal.Value = matchItem.Value !== undefined ? matchItem.Value : "";
          }
        }
      }

      const result = await CallSaveApiForIntakeSheet(JSON.stringify(bdClone));
      setCollectionOfEPAT(updatedCollection);
      setBeforeParsingEPATData(bdClone); // save the clone
      return result;
    } catch (error) {
      console.error(error?.message);
      return false;
    }
  };

  /* ── save — mirrors AOB exactly ── */
  const saveFunction = async () => {
    try {
      setLoading(true);
      const s = stateRef.current;
      const updatedCollection = updateCollection(s.collectionOfEPAT, s);
      const result = await formJsonRequest(updatedCollection, s.BeforeParsingEPATData);
      setLoading(false);
      return result;
    } catch (error) {
      setLoading(false);
      console.error(error?.message);
      return false;
    }
  };


  /* ── provider field ── */
  const ProviderField = () =>
    providerOptions.length === 1 ? (
      <input
        className="acu__input"
        type="text"
        value={
          providerOptions[0]?.officeName || providerOptions[0]?.label || ""
        }
        readOnly
      />
    ) : (
      <Select
        options={providerOptions}
        value={selectedProvider}
        getOptionLabel={(e) => e?.officeName || e?.label}
        getOptionValue={(e) => e?.value}
        onChange={onProviderChange}
        tabSelectsValue={false}
        classNamePrefix="react-select"
        placeholder="Select Provider"
      />
    );

  /* ── render ── */
  return (
    <>
      {loading ? (
        <SpinnerLoader message="Loading form data…" />
      ) : (
        <div className="acu__wrapper">
          {/* Title */}
          <div className="acu__title-block">
            <div className="acu__title-main">
              ACUPUNCTURE INFORMED CONSENT TO TREAT
            </div>
          </div>

          {/* Provider Name */}
          <div className="acu__field-row" style={{ marginBottom: "16px" }}>
            <span className="acu__field-label">
              <b>Provider Name:</b>
            </span>
            <div className="acu__field-control">

                <input
                  className="acu__input"
                  type="text"
                  value={tokenData?.providerName || ""}
                  readOnly
                  disabled
                />

              <small style={{ color: "#6b7280" }}>
                (Print hospital or health care provider name)
              </small>
            </div>
          </div>

          {/* Consent text */}
          <div className="acu__consent-sections">
            <div className="acu__consent-block">
              <p>
                I hereby request and consent to the performance of acupuncture
                treatments and other procedures within the scope of the practice
                of acupuncture on me (or on the patient named below, for whom I
                am legally responsible:{" "}
                <input
                  className="acu__input"
                  style={{
                    display: "inline-block",
                    width: 220,
                    verticalAlign: "middle",
                  }}
                  placeholder="Guardian Name"
                  value={guardianName}
                  onChange={(e) => setGuardianName(e.target.value)}
                />
                ) by the acupuncturist named below and/or other licensed
                acupuncturists who now or in the future treat me while employed
                by, working or associated with or serving as back-up for the
                acupuncturist named below, including those working at the clinic
                or office listed below or any other office or clinic, whether
                signatories to this form or not.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                I understand that methods of treatment may include, but are not
                limited to, acupuncture, moxibustion, cupping, electrical
                stimulation, Tui-Na (Chinese massage), Chinese herbal medicine,
                and nutritional counseling. I understand that the herbs may need
                to be prepared and the teas consumed according to the
                instructions provided orally and in writing. The herbs may have
                an unpleasant smell or taste. I will immediately notify a member
                of the clinical staff of any unanticipated or unpleasant effects
                associated with the consumption of the herbs.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                I have been informed that acupuncture is a generally safe method
                of treatment, but that it may have some side effects, including
                bruising, numbness or tingling near the needling sites that may
                last a few days, and dizziness or fainting. Burns and/or
                scarring are a potential risk of moxibustion and cupping, or
                when treatment involves the use of heat lamps. Bruising is a
                common side effect of cupping. Unusual risks of acupuncture
                include spontaneous miscarriage, nerve damage and organ
                puncture, including lung puncture (pneumothorax). Infection is
                another possible risk, although the clinic uses sterile
                disposable needles and maintains a clean and safe environment.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                I understand that while this document describes the major risks
                of treatment, other side effects and risks may occur. The herbs
                and nutritional supplements that have been recommended are
                traditionally considered safe in the practice of Chinese
                Medicine, although some may be toxic in large doses. I
                understand that some herbs may be inappropriate during
                pregnancy. Some possible side effects of taking herbs are
                nausea, gas, stomachache, vomiting, headache, diarrhea, rashes,
                hives, and tingling of the tongue. I will notify a clinical
                staff member if I am or become pregnant.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                I do not expect the clinical staff to be able to anticipate and
                explain all possible risks and complications of treatment, and I
                wish to rely on the clinical staff to exercise judgment during
                the course of treatment which the clinical staff thinks at the
                time, based upon the facts then known, is in my best interest. I
                understand that results are not guaranteed.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                I understand that the clinical and administrative staff may
                review my patient records and lab reports, but all my records
                will be kept confidential and will not be released without my
                written consent.
              </p>
            </div>

            <div className="acu__consent-block">
              <p>
                By voluntarily signing below, I show that I have read, or have
                had read to me, the above consent to treatment, have been told
                about the risks and benefits of acupuncture and other
                procedures, and have had an opportunity to ask questions. I
                intend this consent form to cover the entire course of treatment
                for my present condition and for any future condition(s) for
                which I seek treatment.
              </p>
            </div>

            <div className="acu__consent-block">
              <div className="acu__consent-heading">
                If patient is unable to sign, please take the sign of the
                patient representative.
              </div>
            </div>
          </div>

          {/* ── Name row ── */}
          <div className="acu__grid-row">
            <span className="acu__grid-label">
              <b>Name :</b>
            </span>
            <div>
              <input
                className="acu__input"
                type="text"
                value={patientName}
                disabled
              />
            </div>
            <span className="acu__grid-label">
              <b>Relation with Patient:</b>
            </span>
            <div>
              <input
                className="acu__input"
                type="text"
                placeholder="Relation to Patient (if patient unable to sign)"
                value={relationToPatient}
                onChange={(e) => setRelationToPatient(e.target.value)}
              />
            </div>
          </div>

          {/* ── Signatures header ── */}
          <div className="acu__section">
            <span className="acu__section-title">Signatures</span>
            <span className="acu__section-line" />
          </div>

          {/* ── Signature boxes ── */}
          <div className="acu__grid-row">
            <span className="acu__grid-label">
              <b>Signature :</b>
            </span>
            <div>
              <div
                className={`acu__sign-box${!patientSignImg ? " acu__sign-box--empty" : ""}`}
                onClick={clickPatientSign}
              >
                {patientSignImg && (
                  <img
                    src={patientSignImg}
                    alt="Patient signature"
                    className="acu__sign-img"
                  />
                )}
              </div>
            </div>
            <span className="acu__grid-label">
              <b>Signature Of Provider:</b>
            </span>
            <div>
              {/* Provider signature — display only, not clickable */}
              <div
                className="acu__sign-box acu__sign-box--disabled"
              >
                {providerSignImg && (
                  <img
                    src={providerSignImg}
                    alt="Provider signature"
                    className="acu__sign-img"
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Signature dates ── */}
          <div className="acu__grid-row acu__grid-row--center">
            <span className="acu__grid-label">
              <b>Date of Signature:</b>
            </span>
            <div>
              <DatePicker
                selected={patientSignDate}
                onChange={(d) => setPatientSignDate(d)}
                dateFormat="MM/dd/yyyy"
                className="acu__datepicker"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
            </div>
            <span className="acu__grid-label">
              <b>Date of Provider Signature:</b>
            </span>
            <div>
              <DatePicker
                selected={providerSignDate}
                onChange={(d) => setProviderSignDate(d)}
                dateFormat="MM/dd/yyyy"
                className="acu__datepicker"
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                disabled
              />
            </div>
          </div>

          {/* ── Signature Pad Modal ── */}
          <SignaturePad
            isOpen={sigPadOpen}
            title={
              signRole === RoleType.Patient
                ? "Patient Signature"
                : "Provider Signature"
            }
            existingSign={currentSig}
            onClose={() => setSigPadOpen(false)}
            onSave={(base64) => {
              signUpdatedCall(base64);
              setSigPadOpen(false);
            }}
            onClear={clearSignCall}
          />
        </div>
      )}
    </>
  );
});

export default AcupunctureConsent;
